import { TextInput, styled, css } from 'canoe';
import invert from 'lodash/invert';
import React, { useState, useEffect, useRef } from 'react';

import { useOutsideClick } from 'commons/hooks';

const Box = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.White};
    align-items: center;
    gap: 2px;
    display: flex;
    width: 100px;
  `}
`;

const UnitBox = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 20px;
`;

// Contains a list of css unit string values that we want to display differently
// For example, if the value is "unset", we want to display a "-"
const unitValuesMap: Record<string, string> = {
  unset: '-',
};
const invertUnitValuesMap = invert(unitValuesMap);

// Match a value to get its digit and value. Ie:
// 32px => {value: 32, unit: "px", valueType: "unit}
// 32 => {value: 32, unit: null, valueType: "digit"}
// unset => {value: "unset", unit: null, valueType: "string"}
const getUnitAndValue = (inputValue: string) => {
  const unitMatch = inputValue.match(/([-+]?[0-9]*\.?[0-9]+)(px)?/) ?? [];
  const digit = unitMatch[1];
  const unit = unitMatch[2];

  const isValueJustDigit = !unit && digit && digit.toString() === inputValue;

  if (unit) {
    return {
      unit,
      value: digit,
      valueType: unit ? 'unit' : 'digit',
    };
  }

  if (isValueJustDigit) {
    return {
      unit: 'px',
      value: digit,
      valueType: unit ? 'unit' : 'digit',
    };
  }

  return {
    unit: null,
    value: unitValuesMap[inputValue] ?? inputValue,
    valueType: 'string',
  };
};

interface Props {
  value: string;
  onChange?: (value: string) => void;
  type?: 'text' | 'number' | 'unit';
  disabled?: boolean;
}

export const Input = ({ type, value, onChange, disabled }: Props) => {
  const ref = useRef(null);

  const [internalValue, setInternalValue] = useState(value);
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const { value: valueToDisplay, unit: unitToDisplay, valueType } = getUnitAndValue(internalValue);

  const callOnChange = (inputValue: string) => {
    setInternalValue(inputValue);
    if (!onChange) {
      return;
    }

    if (!inputValue) {
      // Do not propagate empty values
      return;
    }

    if (type === 'unit') {
      const { value: newValue, unit } = getUnitAndValue(inputValue);
      const newUnit = unit;

      if (invertUnitValuesMap[newValue]) {
        onChange(invertUnitValuesMap[newValue]);
        return;
      }

      onChange(`${newValue}${newUnit ?? ''}`);
      return;
    }

    onChange(inputValue);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (Number.isNaN(`${valueToDisplay}`)) {
        return;
      }
      const resolvedValue = valueToDisplay === '-' ? '0' : valueToDisplay;
      const numberValue = parseInt(`${resolvedValue}`, 10);
      // up
      if (e.key === 'ArrowUp') {
        callOnChange(`${numberValue + 1}`);
      }
      // down
      if (e.key === 'ArrowDown') {
        callOnChange(`${numberValue - 1}`);
      }
    }
  };

  useOutsideClick(ref.current, () => {
    if (!onChange) {
      return;
    }

    // If unit type but value is invalid
    const invalidValue =
      type === 'unit' &&
      valueType === 'string' &&
      ['auto', 'inherit', 'unset'].includes(value.toString()) === false;

    // Set value to unset if received an invalid value
    if (!value || value === '' || invalidValue) {
      onChange('unset');
    }
  });

  return (
    <Box ref={ref}>
      <TextInput
        value={valueToDisplay}
        disabled={disabled}
        onChange={(e: any) => {
          callOnChange(e.target.value);
        }}
        onKeyDown={onKeyDown}
        type={type === 'unit' ? 'text' : type}
      />
      {type === 'unit' && <UnitBox>{unitToDisplay}</UnitBox>}
    </Box>
  );
};
